/* eslint-disable react-hooks/exhaustive-deps */
import {useEffect, useState} from 'react'
import {Link, useParams} from 'react-router-dom'
import {Alert, Chip, Snackbar, Typography} from '@mui/material'
import Icon from '@mdi/react'
import {mdiArrowRight} from '@mdi/js'
import CardCustom from 'src/components/elements/CardCustom/CardCustom'
import Loader from 'src/components/elements/Loader/Loader'
import PrimaryButton from 'src/components/elements/Buttons/PrimaryButton/PrimaryButton'
import SecondaryButton from 'src/components/elements/Buttons/SecondaryButton/SecondaryButton'
import SnackbarAlertCustom from 'src/components/elements/SnackbarAlertCustom/SnackbarAlertCustom'
import WarningPatientEmptyData from 'src/components/elements/WarningEmptyCard/WarningPatientEmptyData'
import LayoutContainer from 'src/components/layouts/LayoutContainer/LayoutContainer'
import {PATHS, STATUS} from 'src/constants/constants'
import apiClient from 'src/services/apiClient'
import {useAppSelector} from 'src/hooks'
import {
  buildSecondaryAssumptionResponseBody,
  followTakenAt,
  formatAssumptionDateTime,
  formatSecondaryAssumptionState,
  getInitialResponseForm,
  getAssumptionColumnOptions,
  getVisibleAssumptionFieldIds,
  isNextDoseValid,
  isResponseFormValid,
  isSecondaryAssumptionAnswered,
  SecondaryAssumption,
  SecondaryAssumptionResponseForm as ResponseForm
} from 'src/constants/secondaryAssumptions'
import AssumptionResponseForm from './AssumptionResponseForm'
import NextDoseFields from './NextDoseFields'

// Legacy-stack twin of pages-new/secondaryAssumptions/SecondaryAssumptionResponseNew.
//
// It exists because the reminder carries ONE url while the stack that renders it depends on the
// PSP's newStyleEnabled flag: without this page the link would 404 on every legacy PSP. Both
// routes answer to the same PATHS.SECONDARY_ASSUMPTION_RESPONSE, so therapy-service keeps a single
// ASSUMPTION_RESPONSE_PATH regardless of which portal style a program is on.

// Single crumb, no href: whoever lands here came from a mail or an SMS and has no page to go
// "back" to — and a link to /therapies would be dead on any PSP that hides that sidebar item.
const BREADCRUMBS = [{label: 'Conferma assunzione'}]

const CHIP_COLORS: Record<string, 'success' | 'warning' | 'error' | 'default'> = {
  COMPLETED: 'success',
  PLANNED: 'warning',
  CANCELLED: 'error',
  NOT_ANSWERED: 'default'
}

const SecondaryAssumptionResponse = () => {
  const {secondaryAssumptionId} = useParams<{secondaryAssumptionId: string}>()

  const [assumption, setAssumption] = useState<SecondaryAssumption | undefined>(undefined)
  const [status, setStatus] = useState<string | undefined>(undefined)
  const [statusSubmit, setStatusSubmit] = useState<string | undefined>(undefined)
  const [form, setForm] = useState<ResponseForm>(getInitialResponseForm())
  const [hasJustAnswered, setHasJustAnswered] = useState<boolean>(false)
  // once the patient edits the next dose by hand, moving takenAt no longer drags it along
  const [isNextDoseTouched, setIsNextDoseTouched] = useState<boolean>(false)
  const [showAlreadyAnsweredNotice, setShowAlreadyAnsweredNotice] = useState<boolean>(false)

  const assumptionsColumnOptions = useAppSelector(
    state => getAssumptionColumnOptions(state.pspConfigReducer.data)
  )
  const showStatus = getVisibleAssumptionFieldIds(assumptionsColumnOptions).has('status')

  const fetchAssumption = async () => {
    const result = await apiClient.getSecondaryAssumption(secondaryAssumptionId, setStatus)
    if (result) {
      setAssumption(result)
      // CA3: reopening the link must show the previous answer, not an empty form.
      setForm(getInitialResponseForm(result))
      // A toast (top-right, auto-dismiss) rather than a message sitting permanently in the card:
      // it only needs to be noticed once, on arrival, not kept taking up space next to the form
      // below, which already shows the previous answer precompiled.
      if (isSecondaryAssumptionAnswered(result)) {
        setShowAlreadyAnsweredNotice(true)
      }
    }
  }

  useEffect(() => {
    fetchAssumption()
  }, [secondaryAssumptionId])

  // `formOverride` lets the ✓/✗ tap submit immediately with the form it just produced, without
  // waiting for React to flush `form` state first — see AssumptionResponseForm's `onTakenChoice`.
  // The bottom button still calls this with no argument, reading current `form`.
  const onSubmit = async (formOverride?: ResponseForm) => {
    const submitted = formOverride ?? form
    // the ✓/✗ tap submits on its own: it must not send a next dose the server would reject
    if (!isNextDoseValid(submitted, assumption)) { return }
    const body = buildSecondaryAssumptionResponseBody(submitted, assumption)
    const updated = await apiClient.patchSecondaryAssumption(secondaryAssumptionId, body, setStatusSubmit)
    if (updated) {
      // RF4.4: once moved, the new next dose is the reference - not an edit any more for the next
      // "Aggiorna" (the PATCH response does not carry nextExpectedDate)
      setAssumption(current => ({
        ...current,
        ...updated,
        ...body.nextExpectedDate ? {nextExpectedDate: body.nextExpectedDate} : {}
      } as SecondaryAssumption))
      setHasJustAnswered(true)
    }
  }

  if (status === STATUS.PENDING) {
    return <Loader />
  }

  if (status === STATUS.REJECTED || !assumption) {
    return (
      <LayoutContainer breadcrumbs={BREADCRUMBS}>
        <WarningPatientEmptyData
          message='Promemoria non trovato. Il link potrebbe non essere più valido, oppure il promemoria non è associato al tuo profilo.'
        />
      </LayoutContainer>
    )
  }

  const wasAlreadyAnswered = isSecondaryAssumptionAnswered(assumption)

  return (
    <LayoutContainer breadcrumbs={BREADCRUMBS}>
      <div style={{maxWidth: 640, margin: '0 auto'}}>
        <CardCustom
          title={
            <span style={{display: 'inline-flex', alignItems: 'center', gap: 8, flexWrap: 'wrap'}}>
              {assumption.drugName ?? 'Terapia accessoria'}
              {showStatus && (
                <Chip
                  size='small'
                  label={formatSecondaryAssumptionState(assumption.state)}
                  color={CHIP_COLORS[assumption.state ?? ''] ?? 'default'}
                />
              )}
            </span>
          }
        >
          <Typography variant='body2' color='textSecondary' style={{marginBottom: 24}}>
            {`Promemoria per l'assunzione prevista il ${formatAssumptionDateTime(assumption.expectedDate)}.`}
          </Typography>

          {hasJustAnswered && (
            <Alert severity='success' style={{marginBottom: 24}}>
              Risposta registrata. Puoi modificarla anche in un secondo momento.
            </Alert>
          )}

          <AssumptionResponseForm
            assumption={assumption}
            form={form}
            onChange={value => setForm(isNextDoseTouched ? value : followTakenAt(form, value))}
            disabled={statusSubmit === STATUS.PENDING}
            // Tapping ✓/✗ here already IS a complete, submittable answer — "no" needs nothing
            // else, "yes" defaults to "taken as planned" — so it submits immediately instead of
            // also requiring the button below. That button stays for the *refinement* case:
            // changing "Quando?"/parte del corpo after the fact, or reopening the link to correct.
            onTakenChoice={nextForm => onSubmit(nextForm)}
          />

          {/* RF3.3: shown only when the server says the next dose can be moved from this reminder */}
          {assumption.nextExpectedDate && (
            <div style={{marginTop: 24}}>
              <NextDoseFields
                value={form.nextExpectedDate ?? null}
                onChange={value => {
                  setIsNextDoseTouched(true)
                  setForm({...form, nextExpectedDate: value})
                }}
                disabled={statusSubmit === STATUS.PENDING}
                error={!isNextDoseValid(form, assumption)}
              />
            </div>
          )}

          <div style={{display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 16, marginTop: 32, flexWrap: 'wrap'}}>
            {/* RF3.1/RF3.1.1: the next dose (and the rest of this therapy's history) lives only
                in Diario Paziente > Terapie Accessorie, not on this single-dose page. Enabled
                only once this reminder has an answer (previous or just given): before that,
                there is nothing new to go check yet, and it would just be an extra way to leave
                the page without answering. */}
            {wasAlreadyAnswered || hasJustAnswered ? (
              <Link to={{pathname: PATHS.DIARY, state: {tab: 'secondary', secondaryTherapyId: assumption.secondaryTherapyId}}}>
                <SecondaryButton endIcon={<Icon path={mdiArrowRight} size={0.9} />}>
                  Prossima assunzione
                </SecondaryButton>
              </Link>
            ) : (
              <SecondaryButton disabled endIcon={<Icon path={mdiArrowRight} size={0.9} />}>
                Prossima assunzione
              </SecondaryButton>
            )}

            <PrimaryButton
              disabled={!isResponseFormValid(form, assumption) || statusSubmit === STATUS.PENDING}
              onClick={() => onSubmit()}
            >
              {wasAlreadyAnswered || hasJustAnswered ? 'Aggiorna' : 'Salva'}
            </PrimaryButton>
          </div>
        </CardCustom>
      </div>

      <Snackbar
        open={statusSubmit === STATUS.REJECTED}
        autoHideDuration={4000}
        onClose={() => setStatusSubmit(undefined)}
      >
        <SnackbarAlertCustom severity='error' onClose={() => setStatusSubmit(undefined)}>
          Impossibile registrare la risposta. Riprova.
        </SnackbarAlertCustom>
      </Snackbar>

      <Snackbar
        anchorOrigin={{vertical: 'top', horizontal: 'right'}}
        open={showAlreadyAnsweredNotice}
        autoHideDuration={4000}
        onClose={() => setShowAlreadyAnsweredNotice(false)}
      >
        <SnackbarAlertCustom severity='info' onClose={() => setShowAlreadyAnsweredNotice(false)}>
          Hai già risposto a questo promemoria. Puoi correggere la risposta qui sotto.
        </SnackbarAlertCustom>
      </Snackbar>
    </LayoutContainer>
  )
}

export default SecondaryAssumptionResponse
